import { useNavigate } from "react-router";
import Image from "../images/Image";
import Button from "../buttons/Button";
import useTranslation from "../CustomHooks/useTranslation";
import { breadcrumbsContent } from "./BreadcrumbsContent";
import ToolTip from "../ToolTip/ToolTip";

interface BreadcrumbsProps {
  navPath?: string[];
  heading: string;
  noNavigation?: boolean;
  button?: {
    buttonName: string;
    className?: string;
    buttonOnclick: () => void; // Corrected function type
  } | Record<string, never>;
  secondaryButton?: {
    buttonName: string;
    className?: string;
    buttonOnclick: () => void; // Corrected function type
  };
  hasAddPrivilege?: boolean;
  backButtonNavigation?: string
  links?: (string | number)[] | ((value: boolean) => void);
  upfront?: boolean;
  list?: boolean;
  setAddClicked?: (value: boolean) => void;
  extraPadding?: boolean;
  showNavPath?: boolean;
}
export default function Breadcrumbs({ noNavigation, navPath, button, heading, links = [], backButtonNavigation, secondaryButton, hasAddPrivilege = true, upfront, list, setAddClicked, showNavPath = true }: BreadcrumbsProps) {
  const navigate = useNavigate();
  const { translate, notEnglish } = useTranslation(breadcrumbsContent)

  const hasButton = button && 'buttonName' in button && button.buttonName && hasAddPrivilege;
  const hasSecondaryButton = upfront && secondaryButton?.buttonName;
  const hasAnyButton = hasButton || hasSecondaryButton;

  // List pages: the heading already names the page, so no breadcrumb trail is needed.
  // Detail pages: show only the last two crumbs (parent list + current page); drop the
  // higher-level items. Keep the links sliced in step so navigation indices stay aligned.
  const displayNavPath = list ? undefined : navPath?.slice(-2);
  const displayLinks = !list && navPath && Array.isArray(links) ? links.slice(-2) : links;

  return (
    <div className={`pb-1.5`}>
      <div className="bg-white min-h-[86px] h-auto w-full rounded-lg py-2.5 px-4 sm:px-6 flex flex-wrap gap-y-2 justify-between items-center" dir={`${notEnglish ? 'rtl' : 'ltr'}`}>
        {/* Breadcrumb container - 2/3 width when button present, full width when no button */}
        <div className={`${hasAnyButton ? 'sm:w-2/3 min-w-0 w-full' : 'w-full min-w-0'}`}>
          <div className="flex items-center gap-2.5">
            {backButtonNavigation && <Image src="Back" className='cursor-pointer flex-shrink-0' onClick={() => navigate(backButtonNavigation)} />}
            <div className="min-w-0 flex-1">
              <div className="min-w-0 w-full group relative">
                  <h1 className="text-heading2 overflow-hidden lg:whitespace-nowrap text-ellipsis text-primary_text">{translate(heading)}</h1>
                <ToolTip ToolTipItem={translate(heading)} top="-top-6" />
              </div>
            </div>
          </div>
          {/* Breadcrumb navigation - uses flex with proper truncation */}
          {showNavPath && displayNavPath && <div className="flex mt-2 min-w-0">
            {displayNavPath.map((item, index) => (
              <div className="flex group relative items-center min-w-0 flex-shrink" key={`${index}-breadCrumbs`}>
                {!list ? <><p
                  className={`${index !== displayNavPath.length - 1 ? "text-p_primary cursor-pointer " : ""} text-xs xl:text-body2 overflow-hidden whitespace-nowrap text-ellipsis min-w-0 ${displayLinks.length === 0 ? '' : ''}`}
                  onClick={() => {
                    if ((item === 'View Onboarding Details' || item === 'View Modification Details') && setAddClicked) {
                      setAddClicked(false);
                    } else if (!noNavigation && Array.isArray(displayLinks)) {
                      if (displayLinks[index] === 'back') {
                        navigate(-1);
                      } else {
                        navigate(displayLinks[index] as string);
                      }
                    } else if (Array.isArray(displayLinks)) {
                      navigate(displayLinks[index] as string);
                    }
                  }}
                  data-testid={`${item.replace(/ /g, '_').toLocaleLowerCase()}`}>
                  {translate(item)}{" "}
                </p><ToolTip ToolTipItem={translate(item)} top="-top-6" /></> : <><p
                  className={`${index !== displayNavPath.length - 1 ? "text-p_primary " : ""} text-body2 overflow-hidden whitespace-nowrap text-ellipsis min-w-0`}
                  data-testid={`${item.replace(/ /g, '_').toLocaleLowerCase()}`}>
                  {translate(item)}{" "}
                </p><ToolTip ToolTipItem={translate(item)} /></>}
                {displayNavPath.length !== index + 1 && (
                  <Image src={`${notEnglish ? 'page-light-right' : 'page-light-left'}`} className="mx-1 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>}
        </div>

        {/* Button container - 1/3 width when present */}
        {hasAnyButton && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-2.5 w-full sm:w-auto sm:ml-4">
            {hasSecondaryButton && (
              <Button
                buttonName={translate(secondaryButton?.buttonName || "")}
                className={` ${secondaryButton?.className} border border-p_primary rounded-lg `}
                buttonOnclick={secondaryButton?.buttonOnclick}
                testId={secondaryButton?.buttonName.replace(' ', '_').toLocaleLowerCase()}
                bgColor='bg-white'
                textColor='text-p_primary'
                buttonHight="h-[45px]"
                textSize="text-small"
              />
            )}
            {hasButton && button && 'buttonName' in button && (
              <Button
                buttonName={translate(button.buttonName)}
                className={button.className}
                buttonOnclick={button.buttonOnclick}
                testId={button.buttonName.replace(' ', '_').toLocaleLowerCase()}
                buttonHight="h-[45px]"
                textSize="text-small"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}